import { defineMessages, type MessageDescriptor } from 'react-intl'
import LineSeatIcon from 'LuxKit/Icons/line/LineSeatIcon'
import LinePlayCircleIcon from 'LuxKit/Icons/line/LinePlayCircleIcon'
import LineRestaurantIcon from 'LuxKit/Icons/line/LineRestaurantIcon'
import LineTicketIcon from 'LuxKit/Icons/line/LineTicketIcon'
import LineClochIcon from 'LuxKit/Icons/line/LineClochIcon'
import LineDoorIcon from 'LuxKit/Icons/line/LineDoorIcon'
import LineBagIcon from 'LuxKit/Icons/line/LineBagIcon'
import LineLuggageIcon from 'LuxKit/Icons/line/LineLuggageIcon'
import LineCarIcon from 'LuxKit/Icons/line/LineCarIcon'
import { FlightsClassTypes } from 'constants/flight'

export interface CabinAmenity {
  icon: React.ComponentType
  message: MessageDescriptor
}

export interface CharterCabinContent {
  imageId: string
  inclusions: Array<CabinAmenity>
  baggage: Array<CabinAmenity>
}

const charterCabinMessages = defineMessages({
  economyStandardSeat: {
    id: 'F4yxnIGbGZ-HwUEN66LP',
    defaultMessage: 'Main cabin standard seat',
  },
  entertainmentPersonalDevice: {
    id: 'jOMZWuWfYpzHHZnLuRO7',
    defaultMessage: 'Stream on-demand entertainment to your personal device',
  },
  complimentaryMealAndDrinks: {
    id: 'BQRK_i0lCZoPiyXIpz2F',
    defaultMessage: 'Complimentary meal and drinks service',
  },
  carryOnSevenKg: {
    id: 'SBBJdZLWFogZDnxPBcZ6',
    defaultMessage: 'Carry-on 7kg',
  },
  checkedBagTwentyThreeKg: {
    id: 'dDAOzVfonIy_jZHM2Ry7',
    defaultMessage: 'Checked bag 23kg',
  },
  premiumSeat: {
    id: 'bIUPAClOVQhzkpllLneO',
    defaultMessage: 'Extra legroom and generous seat recline for enhanced comfort',
  },
  priorityCheckInAndBoarding: {
    id: 'ISfGBY0rwClqd4bB8t7K',
    defaultMessage: 'Priority Check In & Boarding',
  },
  luxRowSeat: {
    id: 'bKwuAv_ZCsnbn9Zy-xwN',
    defaultMessage: 'Enjoy more space for two passengers to unwind with a row of four economy seats',
  },
  priorityCheckInSecurityAndBoarding: {
    id: 'QbpmHgAHGI-nie8wujID',
    defaultMessage: 'Priority Check In, Security & Boarding',
  },
  airportLounge: {
    id: '4c9FrzxtAUzvctMPi2aH',
    defaultMessage: 'Relax with airport lounge before your flight',
  },
  luxRowSoloSeat: {
    id: 'Bn6SzfIXtmuGYYDNNia8',
    defaultMessage: 'Enjoy more space to unwind with a row of four economy seats',
  },
  businessSeat: {
    id: 'c0TjZdvglW0txcoTqdP0',
    defaultMessage: 'Luxury lie-flat seats with in-seat power for ultimate comfort and convenience',
  },
  businessTransfer: {
    id: 'DrO4HKiA7kUqNDmfBNgi',
    defaultMessage: 'Home to airport transfers (max 40km) in MEL/SYD/BNE',
  },
  businessEntertainment: {
    id: 'MHWh67Ugh3N8s2fNT9jR',
    defaultMessage: 'Stream on-demand entertainment to provided handheld entertainment screens',
  },
  businessDining: {
    id: '1BEZMl2YLOKp3GOUfM_T',
    defaultMessage: 'Gourmet dining options and drinks service',
  },
  dedicatedCabinCrew: {
    id: '9E_wC1ftCC91UWs2b2B3',
    defaultMessage: 'Dedicated cabin crew for personalised service',
  },
  businessCarryOn: {
    id: '3HU1zkF2KJRkfHNHGR4k',
    defaultMessage: 'Carry-on 2 pieces up to 14kg, max 10kg per piece',
  },
  businessCheckedBag: {
    id: 'nmBacWR105FebYmY5FEM',
    defaultMessage: 'Checked bag 2 pieces, total 46kg, no more than 32kg per piece',
  },
})

export const CHARTER_CABIN_CONTENT: Record<string, CharterCabinContent> = {
  [FlightsClassTypes.ECONOMY]: {
    imageId: 'maldives-economy',
    inclusions: [
      {
        icon: LineSeatIcon,
        message: charterCabinMessages.economyStandardSeat,
      },
      {
        icon: LinePlayCircleIcon,
        message: charterCabinMessages.entertainmentPersonalDevice,
      },
      {
        icon: LineRestaurantIcon,
        message: charterCabinMessages.complimentaryMealAndDrinks,
      },
    ],
    baggage: [
      {
        icon: LineBagIcon,
        message: charterCabinMessages.carryOnSevenKg,
      },
      {
        icon: LineLuggageIcon,
        message: charterCabinMessages.checkedBagTwentyThreeKg,
      },
    ],
  },
  [FlightsClassTypes.PREMIUM]: {
    imageId: 'maldives-economy-plus',
    inclusions: [
      {
        icon: LineSeatIcon,
        message: charterCabinMessages.premiumSeat,
      },
      {
        icon: LinePlayCircleIcon,
        message: charterCabinMessages.entertainmentPersonalDevice,
      },
      {
        icon: LineRestaurantIcon,
        message: charterCabinMessages.complimentaryMealAndDrinks,
      },
      {
        icon: LineTicketIcon,
        message: charterCabinMessages.priorityCheckInAndBoarding,
      },
    ],
    baggage: [
      {
        icon: LineBagIcon,
        message: charterCabinMessages.carryOnSevenKg,
      },
      {
        icon: LineLuggageIcon,
        message: charterCabinMessages.checkedBagTwentyThreeKg,
      },
    ],
  },
  [FlightsClassTypes.LUXROW]: {
    imageId: 'maldives-lux-row',
    inclusions: [
      {
        icon: LineSeatIcon,
        message: charterCabinMessages.luxRowSeat,
      },
      {
        icon: LinePlayCircleIcon,
        message: charterCabinMessages.entertainmentPersonalDevice,
      },
      {
        icon: LineRestaurantIcon,
        message: charterCabinMessages.complimentaryMealAndDrinks,
      },
      {
        icon: LineTicketIcon,
        message: charterCabinMessages.priorityCheckInSecurityAndBoarding,
      },
      {
        icon: LineDoorIcon,
        message: charterCabinMessages.airportLounge,
      },
    ],
    baggage: [
      {
        icon: LineBagIcon,
        message: charterCabinMessages.carryOnSevenKg,
      },
      {
        icon: LineLuggageIcon,
        message: charterCabinMessages.checkedBagTwentyThreeKg,
      },
    ],
  },
  [FlightsClassTypes.LUXROW_PAX_ONE]: {
    imageId: 'maldives-lux-row',
    inclusions: [
      {
        icon: LineSeatIcon,
        message: charterCabinMessages.luxRowSoloSeat,
      },
      {
        icon: LinePlayCircleIcon,
        message: charterCabinMessages.entertainmentPersonalDevice,
      },
      {
        icon: LineRestaurantIcon,
        message: charterCabinMessages.complimentaryMealAndDrinks,
      },
      {
        icon: LineTicketIcon,
        message: charterCabinMessages.priorityCheckInSecurityAndBoarding,
      },
      {
        icon: LineDoorIcon,
        message: charterCabinMessages.airportLounge,
      },
    ],
    baggage: [
      {
        icon: LineBagIcon,
        message: charterCabinMessages.carryOnSevenKg,
      },
      {
        icon: LineLuggageIcon,
        message: charterCabinMessages.checkedBagTwentyThreeKg,
      },
    ],
  },
  [FlightsClassTypes.BUSINESS]: {
    imageId: 'maldives-business',
    inclusions: [
      {
        icon: LineSeatIcon,
        message: charterCabinMessages.businessSeat,
      },
      {
        icon: LineCarIcon,
        message: charterCabinMessages.businessTransfer,
      },
      {
        icon: LinePlayCircleIcon,
        message: charterCabinMessages.businessEntertainment,
      },
      {
        icon: LineRestaurantIcon,
        message: charterCabinMessages.businessDining,
      },
      {
        icon: LineTicketIcon,
        message: charterCabinMessages.priorityCheckInSecurityAndBoarding,
      },
      {
        icon: LineClochIcon,
        message: charterCabinMessages.dedicatedCabinCrew,
      },
      {
        icon: LineDoorIcon,
        message: charterCabinMessages.airportLounge,
      },
    ],
    baggage: [
      {
        icon: LineBagIcon,
        message: charterCabinMessages.businessCarryOn,
      },
      {
        icon: LineLuggageIcon,
        message: charterCabinMessages.businessCheckedBag,
      },
    ],
  },
}

export { LineCarIcon }
